/**
 * App shell and routing.
 *
 * The environment badge is fetched once and passed to every screen, including the sign-in page —
 * a badge that only appears after login would leave the most confusing moment unlabelled.
 */

import { useCallback, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type KillSwitchState, type Meta, type User } from './api.js';
import { Shell } from './components/Shell.js';
import { ReservationsPage } from './pages/Reservations.js';
import { RevenuePage } from './pages/Revenue.js';
import { PropertiesPage } from './pages/Properties.js';
import { ActivityPage } from './pages/Activity.js';
import { ControlCenterPage } from './pages/ControlCenter.js';
import { ApprovalsPage } from './pages/Approvals.js';
import { SettingsPage } from './pages/Settings.js';
import { RoadmapPage } from './pages/Roadmap.js';
import { SignInPage } from './pages/SignIn.js';
import { AuthCallbackPage } from './pages/AuthCallback.js';
import { TransactionsPage } from './pages/finance/Transactions.js';
import { BookkeepingPage } from './pages/finance/Bookkeeping.js';
import { ReconciliationPage } from './pages/finance/Reconciliation.js';
import { StatementsPage } from './pages/finance/Statements.js';
import { DebtPage } from './pages/finance/Debt.js';
import { CashFlowPage } from './pages/finance/CashFlow.js';
import { MonthlyReviewPage } from './pages/finance/MonthlyReview.js';
import { BudgetsPage } from './pages/finance/Budgets.js';
import { CapExPage } from './pages/finance/CapEx.js';
import { OverviewPage } from './pages/finance/Overview.js';
import { PropertiesFinancePage } from './pages/finance/Properties.js';
import { ReportsPage } from './pages/finance/Reports.js';
import { ChartPage } from './pages/finance/Chart.js';
import { ClassesPage } from './pages/finance/Classes.js';
import { GuestyListingsPage } from './pages/GuestyListings.js';
import { KnowledgePage } from './pages/knowledge/Knowledge.js';
import { PoliciesPage } from './pages/knowledge/Policies.js';
import { AskPage } from './pages/Ask.js';
import { MaintenancePage } from './pages/Maintenance.js';
import { OperationsPage } from './pages/Operations.js';
import { GuestsPage } from './pages/Guests.js';
import { AutonomyPage } from './pages/Autonomy.js';

export function App(): ReactNode {
  const queryClient = useQueryClient();

  const meta = useQuery({
    queryKey: ['meta'],
    queryFn: () => api.get<Meta>('/api/meta'),
    staleTime: 60_000,
  });

  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<{ user: User }>('/api/me');
      } catch (err) {
        // 401 is the normal signed-out state, not an error worth surfacing.
        if (err instanceof ApiError && err.status === 401) return { user: null };
        throw err;
      }
    },
    retry: false,
  });

  const killSwitch = useQuery({
    queryKey: ['kill-switch'],
    queryFn: () => api.get<{ killSwitch: KillSwitchState }>('/api/kill-switch'),
    enabled: Boolean(me.data?.user),
    refetchInterval: 5_000,
  });

  const onSignedIn = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  const onSignOut = useCallback(() => {
    void api.post('/api/auth/sign-out').finally(() => {
      void queryClient.invalidateQueries();
    });
  }, [queryClient]);

  const badge = meta.data?.badge ?? null;
  const user = me.data?.user ?? null;

  if (me.isLoading) {
    return (
      <div className="signin">
        <div className="card signin-card">
          <h1>Loading…</h1>
        </div>
      </div>
    );
  }

  // Anything but a 401 means the session question was not answered — the API or its database
  // did not reply — and showing the sign-in form there would claim "signed out", which nobody
  // decided. Seen in the plan-8b walkthrough: a DNS failure reaching the hosted development
  // database made `/api/me` a 500 and the shell dropped a signed-in owner onto the sign-in page.
  //
  // Only when nothing is known, though. A failed *refetch* — the window regaining focus after a
  // dropped connection — leaves `me.data` in place, and unmounting the Shell over it would throw
  // away whatever the owner had in hand (a half-typed resolve note) to report a session that is
  // still held. The stale answer stays up; the next successful check replaces it.
  if (me.isError && me.data === undefined) {
    return (
      <div className="signin">
        <div className="card signin-card">
          <h1>Could not check your session</h1>
          <div className="alert">{me.error.message}</div>
          <p className="muted">
            You have not been signed out. The API did not answer the session check, so nothing about
            your session is known yet.
          </p>
          <button type="button" onClick={() => void me.refetch()} disabled={me.isFetching}>
            {me.isFetching ? 'Checking…' : 'Try again'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallbackPage onSignedIn={onSignedIn} />} />
      {!user ? (
        <Route path="*" element={<SignInPage badge={badge} />} />
      ) : (
        <Route
          path="*"
          element={
            <Shell
              badge={badge}
              user={user}
              onSignOut={onSignOut}
              readOnly={killSwitch.data?.killSwitch.platformReadOnly ?? false}
            >
              <Routes>
                <Route path="/" element={<Navigate to="/properties" replace />} />
                <Route path="/properties" element={<PropertiesPage />} />
                <Route path="/reservations" element={<ReservationsPage />} />
                <Route path="/revenue" element={<RevenuePage />} />
                <Route path="/agents" element={<ControlCenterPage />} />
                <Route path="/approvals" element={<ApprovalsPage />} />
                <Route path="/finance/transactions" element={<TransactionsPage />} />
                <Route path="/finance/bookkeeping" element={<BookkeepingPage />} />
                <Route path="/finance/reconciliation" element={<ReconciliationPage />} />
                <Route path="/finance/statements" element={<StatementsPage />} />
                <Route path="/finance/debt" element={<DebtPage />} />
                <Route path="/finance/cash" element={<CashFlowPage />} />
                <Route path="/finance/monthly-review" element={<MonthlyReviewPage />} />
                <Route path="/finance/budgets" element={<BudgetsPage />} />
                <Route path="/finance/capex" element={<CapExPage />} />
                <Route path="/finance" element={<OverviewPage />} />
                <Route path="/finance/properties" element={<PropertiesFinancePage />} />
                <Route path="/finance/reports" element={<ReportsPage />} />
                <Route path="/finance/chart" element={<ChartPage />} />
                <Route path="/finance/classes" element={<ClassesPage />} />
                <Route path="/guesty-listings" element={<GuestyListingsPage />} />
                <Route path="/ask" element={<AskPage />} />
                <Route path="/operations" element={<OperationsPage />} />
                <Route path="/guests" element={<GuestsPage />} />
                <Route path="/autonomy" element={<AutonomyPage />} />
                <Route path="/maintenance" element={<MaintenancePage />} />
                <Route path="/knowledge" element={<KnowledgePage />} />
                <Route path="/knowledge/policies" element={<PoliciesPage />} />
                <Route path="/activity" element={<ActivityPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/roadmap" element={<RoadmapPage />} />
                <Route path="*" element={<Navigate to="/properties" replace />} />
              </Routes>
            </Shell>
          }
        />
      )}
    </Routes>
  );
}
